import type { Branch } from '@/features/auth/types';
import type {
  Allergy,
  AssistanceNeed,
  EkycStatus,
  PatientDetail,
  PatientIdentity,
  Payer,
} from '@/features/patients/types';

export type TicketStatus = 'waiting' | 'in_room' | 'done' | 'cancelled';
export type SeeFirstFlag =
  | 'chest_pain'
  | 'difficulty_breathing'
  | 'child_under5_fever_39'
  | 'severe_bleeding'
  | 'fits_or_confusion'
  | 'stroke_signs'
  | 'severe_allergic_reaction'
  | 'abnormal_vitals';

/** Labels exactly as on the Admin v2 "See first" drawer. */
export const SEE_FIRST_LABELS: Record<SeeFirstFlag, string> = {
  chest_pain: 'Chest pain',
  difficulty_breathing: 'Difficulty breathing',
  child_under5_fever_39: 'Child under 5 with fever of 39 °C or more',
  severe_bleeding: 'Severe bleeding',
  fits_or_confusion: 'Fits or confusion',
  stroke_signs: 'Stroke signs (face, arm, speech)',
  severe_allergic_reaction: 'Severe allergic reaction',
  abnormal_vitals: 'Very high or very low vitals',
};

export interface VitalFlag {
  field: string;
  level: 'high' | 'low';
  label: string;
}

export interface Vitals {
  id: string;
  recordedAt: string;
  recordedBy: { id: string; fullName: string } | null;
  bpSystolic: number | null;
  bpDiastolic: number | null;
  pulse: number | null;
  temperatureC: number | null;
  spo2: number | null;
  pain: number | null;
  weightKg: number | null;
  heightCm: number | null;
  glucoseMmol: number | null;
  glucoseContext: 'random' | 'fasting' | null;
  bmi: number | null;
  flags: VitalFlag[];
}

export interface Service {
  id: string;
  code: string;
  name: string;
  durationMinutes: number | null;
  depositSen?: number | null;
  requiresTravelDate?: boolean;
}

export interface TicketCover {
  status: 'covered' | 'not_covered';
  checkedAt: string;
  memberNo: string | null;
  leftSen: number | null;
  note: string | null;
}

/** RL-940 "Running late? Tell the desk" from the patient app (30 = 30 or more). */
export interface LateNotice {
  minutes: 10 | 20 | 30;
  sentAt: string;
}

/** A reported allergy is patient-entered: not in the record until a doctor confirms it. */
export interface ReportedAllergy {
  substance: string;
  reaction: string | null;
  severity: 'mild' | 'moderate' | 'severe' | null;
}

/** RL-941 day-before check-in from the app, as the counter sees it (`AppCheckInStaffDto`). */
export interface AppCheckIn {
  label: 'Checked in from the app' | 'Check-in started in the app';
  completedAt: string | null;
  updatedAt: string;
  detailsConfirmedAt: string | null;
  payer: {
    choice: 'self' | 'coverage';
    coverage: { id: string; payerName: string; memberNo: string | null } | null;
    setAt: string;
  } | null;
  reason: string | null;
  allergies: {
    none: boolean | null;
    reported: ReportedAllergy[];
    toConfirm: boolean;
    setAt: string;
  } | null;
  assistance: {
    needs: AssistanceNeed[];
    note: string | null;
    setAt: string;
  } | null;
}

/** The app-chosen cover no longer qualifies at check-in: the visit is self-pay for now. */
export interface PayerNotice {
  code: 'APP_COVERAGE_NOT_USABLE';
  message: string;
}

export interface QueueTicket {
  id: string;
  number: number;
  clinicDate: string;
  status: TicketStatus;
  source: 'walk_in' | 'appointment';
  arrivedAt: string;
  calledAt: string | null;
  doneAt: string | null;
  room: string | null;
  seeFirst: { flag: SeeFirstFlag; note: string | null; at: string } | null;
  payer: Payer | null;
  cover: TicketCover | null;
  arrivedLate: boolean;
  bookedFor: string | null;
  /** RL-1664: an unchecked membership from the app (older APIs omit it). */
  appCover?: AppCover | null;
  /** RL-1694/1695: what the patient said in the app; null for walk-ins. */
  patientReason?: string | null;
  /**
   * RL-1668: where the booking was made: `app` (the patient app) or `clinic`
   * (counter, phone, follow-up, waitlist); null for a walk-in. Older APIs omit it.
   */
  bookedIn?: 'app' | 'clinic' | null;
  lateNotice?: LateNotice | null;
  appCheckIn?: AppCheckIn | null;
  patient: {
    id: string;
    fullName: string;
    assistanceNeeds: AssistanceNeed[];
    dateOfBirth: string | null;
    ekycStatus: EkycStatus;
    ekycVerifiedAt: string | null;
    allergies: Pick<
      Allergy,
      | 'id'
      | 'substance'
      | 'reaction'
      | 'status'
      | 'fromMergedRecord'
      | 'fromRecord'
    >[];
    identity: PatientIdentity | null;
  };
  service: Pick<Service, 'id' | 'code' | 'name'> | null;
  doctor: { id: string; fullName: string } | null;
  appointmentId: string | null;
  vitals: Vitals | null;
  encounter: { id: string; status: 'open' | 'closed' } | null;
  version: number;
}

export interface QueueSummary {
  clinicDate: string;
  waiting: number;
  inRoom: number;
  done: number;
  bookedToday: number;
  arrivedFromBookings: number;
  averageWaitMinutes: number | null;
  averageWaitYesterdayMinutes: number | null;
  waitingWalkIns: number;
  waitingBooked: number;
}

export interface QueueDisplay {
  clinicDate: string;
  nowCalling: { number: number; room: string | null; calledAt: string } | null;
  next: number[];
  called: { number: number; room: string | null }[];
}

export interface Appointment {
  id: string;
  startsAt: string;
  durationMinutes: number | null;
  status: 'booked' | 'checked_in' | 'cancelled' | 'no_show';
  /** The desk's note (not the patient's reason). */
  notes: string | null;
  /** RL-1694/1695: what the patient said in the app; null = none given. */
  patientReason?: string | null;
  patient: {
    id: string;
    fullName: string;
    assistanceNeeds: AssistanceNeed[];
    phone?: string | null;
    ekycStatus?: EkycStatus;
    arrivalPassToken?: string | null;
  };
  service: Pick<Service, 'id' | 'code' | 'name' | 'requiresTravelDate'>;
  doctor: { id: string; fullName: string } | null;
  branch: Branch;
  queueTicket: { id: string; number: number; status: TicketStatus } | null;
  repeat: 'once' | 'every_3_months' | 'every_6_months';
  seriesId: string | null;
  travelDate: string | null;
  deposit: {
    amountSen: number;
    status: 'pending' | 'paid' | 'refunded' | 'forfeited';
    paidAt: string | null;
    method: 'cash' | 'card' | null;
  } | null;
  rebook: {
    requestedAt: string;
    event: { id: string; type: string; description: string } | null;
  } | null;
  arrivedLate: boolean;
  lateNotice?: LateNotice | null;
  appCheckIn?: AppCheckIn | null;
  checkedInAt: string | null;
  noShowAt: string | null;
  cancelledAt: string | null;
  cancelReason: string | null;
  version: number;
}

export interface AppointmentDetail extends Appointment {
  series: { id: string; startsAt: string; status: Appointment['status'] }[];
  messages: {
    id: string;
    channel: 'app' | 'sms' | 'whatsapp';
    kind: string;
    body: string;
    status: 'simulated' | 'suppressed';
    suppressedReason: string | null;
    createdAt: string;
  }[];
  noShows: number;
}

/** POST /appointments/:id/check-in and the booking inside a scan result. */
export type CheckedInAppointment = Appointment & {
  payerNotice?: PayerNotice | null;
};

/** POST /queue/scan (`ScanResultDto`). */
export interface ScanResult {
  result: 'checked_in' | 'walk_in';
  /** app_pass = the app's rotating pass (OMP1…); counter_token = the stand-in OMC-… token. */
  via: 'app_pass' | 'counter_token';
  patient: { id: string; fullName: string; ekycStatus?: EkycStatus };
  appointment: CheckedInAppointment | null;
  ticket: { id: string; number: number; status: TicketStatus } | null;
  payerNotice: PayerNotice | null;
  message: string;
}

export interface ArrivalLookup {
  patient: PatientDetail | null;
  bookingsToday: ArrivalBooking[];
  elsewhere: ArrivalBooking[];
}
export interface ArrivalBooking {
  id: string;
  startsAt: string;
  branch: { id: string; name: string };
  service: { id: string; name: string };
  doctor: { id: string; fullName: string } | null;
}

export interface CoverCheck {
  status: 'covered' | 'not_covered';
  payer: Payer | null;
  message: string;
  memberNo: string | null;
  yearlyLimitSen: number | null;
  usedSen: number | null;
  leftSen: number | null;
  coPayment: string | null;
  notCovered: string | null;
  checkedAt: string;
  source: string;
  /**
   * RL-1664: a membership the patient typed in the app, not checked at the
   * counter yet; null (or absent on older APIs) otherwise.
   */
  cardCheck?: {
    needed: true;
    coverageId: string;
    /** The visit is self-pay: saving with the card checked sets this payer. */
    setsPayer: boolean;
    ifChecked: 'covered' | 'not_covered';
    /** The role may mark it checked (patients:update). */
    allowed: boolean;
  } | null;
}

/** RL-1664: a membership typed in the app that Check cover can confirm. */
export interface AppCover {
  coverageId: string;
  payer: Payer;
  memberNo: string;
}

export interface VitalsInput {
  bpSystolic?: number;
  bpDiastolic?: number;
  pulse?: number;
  temperatureC?: number;
  spo2?: number;
  pain?: number;
  weightKg?: number;
  heightCm?: number;
  glucoseMmol?: number;
  glucoseContext?: 'random' | 'fasting';
}
